import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useStyles } from './styles';

/** Components */
import Skeleton from '@mui/material/Skeleton';
import PageContainer from '../../components/PageContainer';
import { PageHeader } from '../../components/ui';
import {
    Grid,
    Paper,
    Typography,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Switch,
    FormControlLabel,
    useTheme,
    Tabs,
    Tab,
    Chip,
    InputAdornment,
    Tooltip,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

/** Selectors */
import { MockupsStats, Notifications } from '../../store/slices';
import { IStore } from '../../store/types';
import MockupStatsFilters from '../../components/MockupStatsFilters';
import Dayjs from 'dayjs';
import { runMockupRecognition } from '../../api';

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement, Title, ChartDataLabels);

// ─── Tag category definitions ────────────────────────────────────────────────

const DOG_BREEDS = new Set([
    'dog',
    'poodle', 'chihuahua', 'bulldog', 'dachshund', 'shepherd', 'labrador', 'sheepdog',
    'beagle', 'husky', 'boxer', 'german shepherd', 'pomeranian', 'corgi', 'retriever',
    'golden retriever', 'pug', 'rottweiler', 'french bulldog', 'border collie',
    'doberman', 'schnauzer', 'greyhound', 'samoyed', 'shiba inu', 'pitbull', 'pointer',
    'cocker', 'dalmatian', 'teddy', 'laboratory', 'french', 'ram',
]);

const CAT_BREEDS = new Set(['cat', 'tabby', 'siamese', 'persian']);

const OTHER_PETS_TAGS = new Set([
    'rabbit', 'bunny', 'guinea pig', 'hamster', 'ferret', 'hedgehog', 'rat', 'mouse',
    'parrot', 'bird', 'fish', 'lizard', 'snake', 'turtle', 'gecko', 'frog', 'reptile',
    'iguana', 'chameleon',
]);

const WILDLIFE_FARM_TAGS = new Set([
    'horse', 'pony', 'pig', 'cow', 'cattle', 'bull', 'goat', 'sheep', 'lamb',
    'chicken', 'duck', 'goose', 'donkey', 'reindeer', 'deer',
    'tiger', 'lion', 'wolf', 'fox', 'bear', 'panda', 'monkey', 'elephant',
    'penguin', 'eagle', 'owl', 'bat', 'raccoon', 'squirrel', 'rodent',
    'butterfly', 'spider', 'animal', 'pet',
]);

const PEOPLE_GROUPS = new Set([
    'man', 'person', 'woman', 'girl', 'child', 'boy', 'baby', 'couple', 'family',
    'mother', 'father', 'daughter', 'group', 'grandmother', 'grandfather', 'toddler',
    'newborn', 'face male', 'face female', 'bride', 'groom', 'family photo',
    'wedding party', 'bridesmaid', 'son',
]);

const POSE_TYPES = new Set([
    'sit', 'lay', 'stand', 'see', 'catch', 'hang', 'lie', 'sleep', 'hug', 'jump',
    'kneel', 'walk', 'run', 'pose', 'close-up', 'smile', 'hold', 'carry',
]);

const COLOR_TAGS = new Set([
    'white', 'black', 'brown', 'blue', 'red', 'gray', 'pink', 'green', 'yellow',
    'purple', 'gold', 'silver', 'tan', 'orange', 'beige',
]);

const THEME_TAGS = new Set([
    'christmas', 'wedding', 'birthday', 'halloween', 'graduation', 'party',
    'easter', 'thanksgiving', 'valentine', 'army', 'police', 'santa claus',
    'halloween costume', 'christmas hat', 'sticker', 'pillow', 'bow tie', 'bow',
    'neckband', 'headscarf', 'scarf',
]);

const CLOTHING_TAGS = new Set([
    'shirt', 'dress', 'hat', 'jacket', 'wear', 'coat', 'suit', 'sweater', 'hoodie',
    't shirt', 't-shirt', 'jeans', 'pant', 'skirt', 'vest', 'uniform', 'sleepwear',
    'boot', 'shoe', 'sneaker', 'cap', 'sunglasses', 'goggles', 'glove', 'shorts',
    'blouse', 'robe', 'costume', 'swimwear', 'legging', 'polo shirt',
]);

type TagCategory = 'Dog' | 'Cat' | 'Animal' | 'People' | 'Pose' | 'Color' | 'Theme' | 'Clothing' | 'Other';

const CATEGORY_COLORS: Record<TagCategory, string> = {
    Dog: '#3b82f6',
    Cat: '#8b5cf6',
    Animal: '#22c55e',
    People: '#ef4444',
    Pose: '#14b8a6',
    Color: '#6366f1',
    Theme: '#f97316',
    Clothing: '#0ea5e9',
    Other: '#9ca3af',
};

function getTagCategory(tagName: string): TagCategory {
    const name = tagName.toLowerCase();
    if (DOG_BREEDS.has(name)) return 'Dog';
    if (CAT_BREEDS.has(name)) return 'Cat';
    if (PEOPLE_GROUPS.has(name)) return 'People';
    if (POSE_TYPES.has(name)) return 'Pose';
    if (COLOR_TAGS.has(name)) return 'Color';
    if (THEME_TAGS.has(name)) return 'Theme';
    if (CLOTHING_TAGS.has(name)) return 'Clothing';
    if (OTHER_PETS_TAGS.has(name) || WILDLIFE_FARM_TAGS.has(name)) return 'Animal';
    return 'Other';
}

function categorizeTags(tags: any[]) {
    const result: Record<string, any[]> = {
        dogBreeds: [],
        catBreeds: [],
        otherPetsTags: [],
        wildlifeTags: [],
        peopleGroups: [],
        poseTypes: [],
        colorTags: [],
        themeTags: [],
        clothingTags: [],
    };
    tags.forEach(tag => {
        const name = tag.tagName.toLowerCase();
        if (DOG_BREEDS.has(name)) result.dogBreeds.push(tag);
        else if (CAT_BREEDS.has(name)) result.catBreeds.push(tag);
        if (OTHER_PETS_TAGS.has(name)) result.otherPetsTags.push(tag);
        if (WILDLIFE_FARM_TAGS.has(name)) result.wildlifeTags.push(tag);
        if (PEOPLE_GROUPS.has(name)) result.peopleGroups.push(tag);
        if (POSE_TYPES.has(name)) result.poseTypes.push(tag);
        if (COLOR_TAGS.has(name)) result.colorTags.push(tag);
        if (THEME_TAGS.has(name)) result.themeTags.push(tag);
        if (CLOTHING_TAGS.has(name)) result.clothingTags.push(tag);
    });
    Object.keys(result).forEach(k => {
        result[k].sort((a, b) => b.eventsCount - a.eventsCount);
    });
    return result;
}

function buildBarData(
    tags: any[],
    metric: 'eventsCount' | 'orderConversionPercent' | 'addToCartPercent',
    color: string,
    sortByMetric = false,
    limit = 20
): { chartData: any; sortedTags: any[] } {
    let slice = [...tags].slice(0, Math.max(tags.length, limit));
    if (sortByMetric) {
        slice = slice.sort((a, b) => parseFloat(b[metric]) - parseFloat(a[metric]));
    }
    slice = slice.slice(0, limit);
    return {
        chartData: {
            labels: slice.map(t => t.tagName),
            datasets: [{
                label: metric === 'eventsCount' ? 'Events' : metric === 'orderConversionPercent' ? 'Order Conv. %' : 'Add-to-Cart %',
                data: slice.map(t => parseFloat(t[metric])),
                backgroundColor: color,
                borderRadius: 4,
            }],
        },
        sortedTags: slice,
    };
}

const fmtNum = (v: number) =>
    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(2)}M`
    : v >= 1000    ? `${(v / 1000).toFixed(1)}k`
    : `${v}`;

const barOptions = (isPercent: boolean, sortedTags: any[]): any => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 56 } },
    plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
            mode: 'nearest' as const,
            intersect: true,
            displayColors: false,
            callbacks: {
                title: (items: any[]) => {
                    const tag = sortedTags[items[0]?.dataIndex];
                    return tag?.tagName ?? '';
                },
                label: () => '',
                afterBody: (items: any[]) => {
                    const tag = sortedTags[items[0]?.dataIndex];
                    if (!tag) return [];
                    return [
                        `📊 Events:       ${Number(tag.eventsCount).toLocaleString()}`,
                        `🛒 Add-to-Cart:  ${tag.addToCartPercent}%`,
                        `✅ Order Conv.:  ${tag.orderConversionPercent}%`,
                    ];
                },
            },
        },
        datalabels: {
            anchor: 'end' as const,
            align: 'end' as const,
            clip: false,
            formatter: (value: number) => isPercent
                ? `${Number(value).toFixed(2)}%`
                : fmtNum(value),
            color: '#374151',
            font: { size: 11, weight: 'bold' as const },
        },
    },
    scales: {
        x: {
            ticks: {
                callback: (v: any) => isPercent
                    ? `${v}%`
                    : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v,
                font: { size: 11 },
                maxTicksLimit: 6,
            },
            grid: { color: 'rgba(0,0,0,0.05)' },
        },
        y: {
            ticks: {
                font: { size: 12, weight: '500' as const },
                crossAlign: 'far' as const,
            },
        },
    },
});

const DEFAULT_PAGING_STATE = {
    maxDate: Dayjs(new Date()).format('YYYY-MM-DD'),
    minDate: Dayjs(new Date()).format('YYYY-MM-DD'),
};

const REQUEST_DELAY_MS = 300;
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// ─── Sub-component: Analytics Section ─────────────────────────────────────────

function AnalyticsSection({
    title,
    tags,
    volumeColor,
    convColor,
    cartColor,
    limit = 15,
}: {
    title: string;
    tags: any[];
    volumeColor: string;
    convColor: string;
    cartColor: string;
    limit?: number;
}) {
    if (!tags || tags.length === 0) return null;

    const itemCount = Math.min(limit, tags.length);
    const chartHeight = itemCount * 36 + 40;

    const { chartData: byVolume, sortedTags: volumeTags } = buildBarData(tags, 'eventsCount', volumeColor, false, limit);
    const { chartData: byConv,   sortedTags: convTags   } = buildBarData(tags, 'orderConversionPercent', convColor, true, limit);
    const { chartData: byCart,   sortedTags: cartTags   } = buildBarData(tags, 'addToCartPercent', cartColor, true, limit);

    const topConv = [...tags].sort((a, b) => parseFloat(b.orderConversionPercent) - parseFloat(a.orderConversionPercent))[0];
    const topVolume = tags[0];
    const topCart = [...tags].sort((a, b) => parseFloat(b.addToCartPercent) - parseFloat(a.addToCartPercent))[0];

    return (
        <Box sx={{ mb: 5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: 15 }}>{title}</Typography>
            <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                    { label: 'Most Popular', value: topVolume?.tagName, sub: topVolume?.eventsCount?.toLocaleString() + ' events', color: volumeColor },
                    { label: 'Best Conv. Rate', value: topConv?.tagName, sub: topConv?.orderConversionPercent + '% ordered', color: convColor },
                    { label: 'Best Add-to-Cart', value: topCart?.tagName, sub: topCart?.addToCartPercent + '% cart', color: cartColor },
                ].map(card => (
                    <Grid item xs={12} sm={4} key={card.label}>
                        <Box sx={{ p: 1.5, borderRadius: 1, border: `2px solid ${card.color}22`, background: `${card.color}11` }}>
                            <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: card.color, textTransform: 'capitalize' }}>{card.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{card.sub}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            By Event Volume
                        </Typography>
                        <Box sx={{ position: 'relative', height: chartHeight }}>
                            <Bar data={byVolume} options={barOptions(false, volumeTags)} />
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            By Order Conversion %
                        </Typography>
                        <Box sx={{ position: 'relative', height: chartHeight }}>
                            <Bar data={byConv} options={barOptions(true, convTags)} />
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            By Add-to-Cart %
                        </Typography>
                        <Box sx={{ position: 'relative', height: chartHeight }}>
                            <Bar data={byCart} options={barOptions(true, cartTags)} />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type SortCol = 'tagName' | 'eventsCount' | 'eventsPercent' | 'eventsToCart' | 'addToCartPercent' | 'eventsOrdered' | 'orderConversionPercent';

const MockupsStatsPage = () => {
    const { mockupsStats, status, queryParams } = useSelector<IStore, { mockupsStats: any; status: string; queryParams: any }>(state => ({
        mockupsStats: state.mockupsStats.data,
        status: state.mockupsStats.status,
        queryParams: state.mockupsStats.queryParams,
    }));
    const dispatch = useDispatch();
    const blockRef = useRef(null);
    const [columnsValue, setColumnsValue] = React.useState(4);
    const classes = useStyles({ columnsValue });
    const [popup, setPopup] = useState<JSX.Element | null>(null);
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const theme = useTheme();

    const getDefaultRecognitionDates = () => ({
        minDate: queryParams?.minDate || DEFAULT_PAGING_STATE.minDate,
        maxDate: queryParams?.maxDate || DEFAULT_PAGING_STATE.maxDate,
    });

    const [recognizeDialogOpen, setRecognizeDialogOpen] = useState(false);
    const [recognizeLoading, setRecognizeLoading] = useState(false);
    const [recognizeError, setRecognizeError] = useState<string | null>(null);
    const [recognizeDates, setRecognizeDates] = useState<{ minDate: string; maxDate: string }>(() => getDefaultRecognitionDates());
    const [splitByHalfDay, setSplitByHalfDay] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // General Table state
    const [tableSearch, setTableSearch] = useState('');
    const [sortCol, setSortCol] = useState<SortCol>('eventsCount');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
    const [categoryFilter, setCategoryFilter] = useState<string>('All');

    const loadOrders = (qp: any) => {
        dispatch<any>(MockupsStats.getMockupsStats(qp));
    };

    const handleOpenRecognitionDialog = () => {
        setRecognizeError(null);
        setRecognizeDates(getDefaultRecognitionDates());
        setSplitByHalfDay(false);
        setRecognizeDialogOpen(true);
    };

    const handleCloseRecognitionDialog = (_event?: object, _reason?: 'backdropClick' | 'escapeKeyDown') => {
        if (recognizeLoading) return;
        setRecognizeDialogOpen(false);
    };

    const handleRecognitionDateChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setRecognizeDates(prev => ({ ...prev, [name]: value }));
    };

    const handleTriggerRecognition = async () => {
        if (!recognizeDates.minDate || !recognizeDates.maxDate) {
            setRecognizeError('Please select both dates');
            return;
        }
        const startDate = Dayjs(recognizeDates.minDate);
        const endDate = Dayjs(recognizeDates.maxDate);
        if (!startDate.isValid() || !endDate.isValid()) {
            setRecognizeError('Invalid date format');
            return;
        }
        if (startDate.isAfter(endDate)) {
            setRecognizeError('Minimum date cannot be greater than maximum date');
            return;
        }
        setRecognizeLoading(true);
        setRecognizeError(null);
        const totalDays = endDate.diff(startDate, 'day') + 1;
        const successRequests: string[] = [];
        const failedRequests: string[] = [];
        for (let offset = 0; offset < totalDays; offset += 1) {
            const currentDate = startDate.add(offset, 'day').format('YYYY-MM-DD');
            if (splitByHalfDay) {
                const firstHalf = `${currentDate} (00:00-11:59)`;
                try {
                    await runMockupRecognition({ createdAt_min: `${currentDate} 00:00:00`, createdAt_max: `${currentDate} 11:59:59` });
                    successRequests.push(firstHalf);
                } catch (error: any) {
                    failedRequests.push(firstHalf);
                }
                await wait(REQUEST_DELAY_MS);
                const secondHalf = `${currentDate} (12:00-23:59)`;
                try {
                    await runMockupRecognition({ createdAt_min: `${currentDate} 12:00:00`, createdAt_max: `${currentDate} 23:59:59` });
                    successRequests.push(secondHalf);
                } catch (error: any) {
                    failedRequests.push(secondHalf);
                }
            } else {
                try {
                    await runMockupRecognition({ createdAt_min: currentDate, createdAt_max: currentDate });
                    successRequests.push(currentDate);
                } catch (error: any) {
                    failedRequests.push(currentDate);
                }
            }
            if (offset !== totalDays - 1) await wait(REQUEST_DELAY_MS);
        }
        setRecognizeLoading(false);
        const totalRequests = splitByHalfDay ? totalDays * 2 : totalDays;
        if (failedRequests.length === 0) {
            dispatch(Notifications.setNotifications({ text: `Recognition triggered successfully for all ${totalRequests} request${totalRequests > 1 ? 's' : ''}`, type: 'success' }));
            setRecognizeDialogOpen(false);
        } else if (successRequests.length === 0) {
            const message = `Recognition failed for all ${totalRequests} request${totalRequests > 1 ? 's' : ''}`;
            setRecognizeError(message);
            dispatch(Notifications.setNotifications({ text: message, type: 'error' }));
        } else {
            const message = `Recognition completed: ${successRequests.length} successful, ${failedRequests.length} failed`;
            setRecognizeError(`Errors for: ${failedRequests.join(', ')}`);
            dispatch(Notifications.setNotifications({ text: message, type: 'warning' }));
        }
    };

    useEffect(() => {
        loadOrders({
            ...queryParams,
            minDate: queryParams?.minDate || DEFAULT_PAGING_STATE.minDate,
            maxDate: queryParams?.maxDate || DEFAULT_PAGING_STATE.maxDate,
        });
    }, []);

    const rawTags: any[] = mockupsStats?.tagStats?.tags || [];

    // Enrich tags with category
    const enrichedTags = useMemo(() =>
        rawTags.map(tag => ({ ...tag, _category: getTagCategory(tag.tagName) })),
    [rawTags]);

    // Filtered & sorted tags for General Table
    const filteredTags = useMemo(() => {
        let list = enrichedTags.filter(t => {
            const matchSearch = tableSearch === '' || t.tagName.toLowerCase().includes(tableSearch.toLowerCase());
            const matchCat = categoryFilter === 'All' || t._category === categoryFilter;
            return matchSearch && matchCat;
        });
        list = [...list].sort((a, b) => {
            const va = sortCol === 'tagName' ? a[sortCol] : parseFloat(a[sortCol]) || 0;
            const vb = sortCol === 'tagName' ? b[sortCol] : parseFloat(b[sortCol]) || 0;
            if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb as string) : (vb as string).localeCompare(va);
            return sortDir === 'desc' ? (vb as number) - (va as number) : (va as number) - (vb as number);
        });
        return list;
    }, [enrichedTags, tableSearch, categoryFilter, sortCol, sortDir]);

    const handleSort = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir(d => d === 'desc' ? 'asc' : 'desc');
        } else {
            setSortCol(col);
            setSortDir('desc');
        }
    };

    const sortArrow = (col: SortCol) => sortCol === col ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';

    // Analytics: categorized
    const categories = useMemo(() => categorizeTags(rawTags), [rawTags]);

    // Top converters across all tags (min 1000 events)
    const topConverters = useMemo(() =>
        [...rawTags]
            .filter(t => t.eventsCount >= 1000)
            .sort((a, b) => parseFloat(b.orderConversionPercent) - parseFloat(a.orderConversionPercent))
            .slice(0, 20),
    [rawTags]);

    // Global insights
    const insights = useMemo(() => {
        if (!rawTags.length) return null;
        const filtered1k = rawTags.filter(t => t.eventsCount >= 1000);
        const bestConv = [...filtered1k].sort((a, b) => parseFloat(b.orderConversionPercent) - parseFloat(a.orderConversionPercent))[0];
        const bestCart = [...filtered1k].sort((a, b) => parseFloat(b.addToCartPercent) - parseFloat(a.addToCartPercent))[0];
        const mostViewed = rawTags[0];
        const bestDogConv = [...(categories.dogBreeds || [])].sort((a, b) => parseFloat(b.orderConversionPercent) - parseFloat(a.orderConversionPercent))[0];
        const bestPoseConv = [...(categories.poseTypes || [])].sort((a, b) => parseFloat(b.orderConversionPercent) - parseFloat(a.orderConversionPercent))[0];
        return { bestConv, bestCart, mostViewed, bestDogConv, bestPoseConv };
    }, [rawTags, categories]);

    const categoryOptions: string[] = ['All', 'Dog', 'Cat', 'Animal', 'People', 'Pose', 'Color', 'Theme', 'Clothing', 'Other'];

    // @ts-ignore
    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Mockups"
                title="Tag stats"
                subtitle="Recognition coverage and how each image tag converts to cart and order."
                actions={(
                    <Button variant="contained" onClick={handleOpenRecognitionDialog} disabled={recognizeLoading}>
                        Recognize images
                    </Button>
                )}
            />
            {popup}
            <MockupStatsFilters
                queryParams={{ ...DEFAULT_PAGING_STATE, ...queryParams }}
                loadMockups={loadOrders}
                loading={status === 'loading'}
                columCount={setColumnsValue}
            />

            {mockupsStats && Object.keys(mockupsStats).length !== 0 && status !== 'loading' && (
                <Box sx={{ mt: 4 }}>
                    {/* ── Recognition summary cards ── */}
                    <Typography variant="h6" gutterBottom>Recognition Overview</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {[
                            { label: 'Total Events', value: mockupsStats?.recognitionStats?.totalCount?.toLocaleString() ?? '-', color: '#047d95' },
                            { label: 'Recognized', value: `${mockupsStats?.recognitionStats?.recognizedCount?.toLocaleString() ?? '-'}`, sub: `${mockupsStats?.recognitionStats?.recognizedPercent ?? '-'}%`, color: '#16a34a' },
                            { label: 'Unrecognized', value: `${mockupsStats?.recognitionStats?.unrecognizedCount?.toLocaleString() ?? '-'}`, sub: `${mockupsStats?.recognitionStats?.unrecognizedPercent ?? '-'}%`, color: '#d97706' },
                            { label: 'Errors', value: `${mockupsStats?.recognitionStats?.errorCount?.toLocaleString() ?? '-'}`, sub: `${mockupsStats?.recognitionStats?.errorCountPercent ?? '-'}%`, color: '#dc2626' },
                            { label: 'Unique Tags', value: rawTags.length.toLocaleString(), color: '#7c3aed' },
                            { label: 'Total Recognized Events', value: mockupsStats?.tagStats?.totalRecognizedEvents?.toLocaleString() ?? '-', color: '#0284c7' },
                        ].map(stat => (
                            <Grid item xs={6} sm={4} md={2} key={stat.label}>
                                <Paper sx={{ p: 2, borderLeft: `3px solid ${stat.color}`, height: '100%' }}>
                                    <Typography variant="caption" color="text.secondary" display="block">{stat.label}</Typography>
                                    <Typography variant="h6" sx={{ color: stat.color, fontWeight: 700, lineHeight: 1.2 }}>{stat.value}</Typography>
                                    {stat.sub && <Typography variant="caption" color="text.secondary">{stat.sub}</Typography>}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    {/* ── Tabs ── */}
                    <Tabs
                        value={activeTab}
                        onChange={(_e, v) => setActiveTab(v)}
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
                    >
                        <Tab label={`General Table (${rawTags.length.toLocaleString()} tags)`} />
                        <Tab label="Analytics" />
                    </Tabs>

                    {/* ═══════════════════════════════════════════════════════════
                        Tab 0: General Table
                    ═══════════════════════════════════════════════════════════ */}
                    {activeTab === 0 && (
                        <Box>
                            {/* Toolbar */}
                            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    placeholder="Search tags…"
                                    value={tableSearch}
                                    onChange={e => setTableSearch(e.target.value)}
                                    sx={{ width: 240 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {categoryOptions.map(cat => (
                                        <Chip
                                            key={cat}
                                            label={cat}
                                            size="small"
                                            variant={categoryFilter === cat ? 'filled' : 'outlined'}
                                            onClick={() => setCategoryFilter(cat)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: categoryFilter === cat && cat !== 'All'
                                                    ? CATEGORY_COLORS[cat as TagCategory]
                                                    : undefined,
                                                color: categoryFilter === cat && cat !== 'All' ? '#fff' : undefined,
                                            }}
                                        />
                                    ))}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                    Showing {filteredTags.length.toLocaleString()} of {rawTags.length.toLocaleString()} tags
                                </Typography>
                            </Box>

                            <Paper sx={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ background: theme.palette.action.hover }}>
                                            <th style={{ padding: '8px 6px', textAlign: 'center', width: 40, color: theme.palette.text.secondary }}>#</th>
                                            <th
                                                align="left"
                                                style={{ padding: '8px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                onClick={() => handleSort('tagName')}
                                            >
                                                Tag{sortArrow('tagName')}
                                            </th>
                                            <th style={{ padding: '8px 6px', width: 70 }}>Category</th>
                                            <th
                                                style={{ padding: '8px 10px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}
                                                onClick={() => handleSort('eventsCount')}
                                            >
                                                Events{sortArrow('eventsCount')}
                                            </th>
                                            <th
                                                style={{ padding: '8px 10px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}
                                                onClick={() => handleSort('eventsPercent')}
                                            >
                                                Events %{sortArrow('eventsPercent')}
                                            </th>
                                            <th
                                                style={{ padding: '8px 10px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}
                                                onClick={() => handleSort('eventsToCart')}
                                            >
                                                To Cart{sortArrow('eventsToCart')}
                                            </th>
                                            <th
                                                style={{ padding: '8px 10px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}
                                                onClick={() => handleSort('addToCartPercent')}
                                            >
                                                Cart %{sortArrow('addToCartPercent')}
                                            </th>
                                            <th
                                                style={{ padding: '8px 10px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'right' }}
                                                onClick={() => handleSort('eventsOrdered')}
                                            >
                                                Ordered{sortArrow('eventsOrdered')}
                                            </th>
                                            <th
                                                style={{ padding: '8px 10px', cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'center' }}
                                                onClick={() => handleSort('orderConversionPercent')}
                                            >
                                                Conv. %{sortArrow('orderConversionPercent')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTags.map((tag, i) => {
                                            const conv = parseFloat(tag.orderConversionPercent);
                                            const cart = parseFloat(tag.addToCartPercent);
                                            return (
                                                <tr
                                                    key={tag.tagId}
                                                    style={{ background: i % 2 === 0 ? 'transparent' : theme.palette.action.hover }}
                                                >
                                                    <td style={{ padding: '5px 6px', textAlign: 'center', color: theme.palette.text.disabled, fontSize: 11 }}>{i + 1}</td>
                                                    <td style={{ padding: '5px 10px', fontWeight: 500 }}>{tag.tagName}</td>
                                                    <td style={{ padding: '5px 6px' }}>
                                                        <Chip
                                                            label={tag._category}
                                                            size="small"
                                                            sx={{
                                                                fontSize: 10,
                                                                height: 18,
                                                                bgcolor: `${CATEGORY_COLORS[tag._category as TagCategory]}22`,
                                                                color: CATEGORY_COLORS[tag._category as TagCategory],
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '5px 10px', textAlign: 'right' }}>{tag.eventsCount?.toLocaleString()}</td>
                                                    <td style={{ padding: '5px 10px', textAlign: 'right', color: theme.palette.text.secondary }}>{tag.eventsPercent}%</td>
                                                    <td style={{ padding: '5px 10px', textAlign: 'right' }}>{tag.eventsToCart?.toLocaleString()}</td>
                                                    <td style={{ padding: '5px 10px', textAlign: 'right', color: cart >= 35 ? '#16a34a' : undefined }}>
                                                        {tag.addToCartPercent}%
                                                    </td>
                                                    <td style={{ padding: '5px 10px', textAlign: 'right' }}>{tag.eventsOrdered?.toLocaleString()}</td>
                                                    <td style={{ padding: '5px 10px', textAlign: 'center' }}>
                                                        <Chip
                                                            label={`${tag.orderConversionPercent}%`}
                                                            size="small"
                                                            color={conv >= 18 ? 'success' : conv >= 14 ? 'primary' : conv >= 10 ? 'default' : 'default'}
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {filteredTags.length === 0 && (
                                            <tr>
                                                <td colSpan={9} style={{ padding: '24px', textAlign: 'center', color: theme.palette.text.secondary }}>
                                                    No tags match the current filter
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </Paper>
                        </Box>
                    )}

                    {/* ═══════════════════════════════════════════════════════════
                        Tab 1: Analytics
                    ═══════════════════════════════════════════════════════════ */}
                    {activeTab === 1 && (
                        <Box>
                            {/* ── Global Insights ── */}
                            {insights && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: 15 }}>
                                        🔍 Key Insights
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {[
                                            {
                                                icon: '🏆',
                                                label: 'Best Overall Conv. Rate',
                                                value: insights.bestConv?.tagName,
                                                sub: `${insights.bestConv?.orderConversionPercent}% orders · ${insights.bestConv?.eventsCount?.toLocaleString()} events`,
                                                color: '#16a34a',
                                            },
                                            {
                                                icon: '🛒',
                                                label: 'Best Add-to-Cart Rate',
                                                value: insights.bestCart?.tagName,
                                                sub: `${insights.bestCart?.addToCartPercent}% cart · ${insights.bestCart?.eventsCount?.toLocaleString()} events`,
                                                color: '#0284c7',
                                            },
                                            {
                                                icon: '👁️',
                                                label: 'Most Viewed Tag',
                                                value: insights.mostViewed?.tagName,
                                                sub: `${insights.mostViewed?.eventsCount?.toLocaleString()} events (${insights.mostViewed?.eventsPercent}%)`,
                                                color: '#7c3aed',
                                            },
                                            {
                                                icon: '🐕',
                                                label: 'Top Dog Breed (Conv.)',
                                                value: insights.bestDogConv?.tagName,
                                                sub: `${insights.bestDogConv?.orderConversionPercent}% conv · ${insights.bestDogConv?.eventsCount?.toLocaleString()} events`,
                                                color: '#ea580c',
                                            },
                                            {
                                                icon: '🧘',
                                                label: 'Top Pose (Conv.)',
                                                value: insights.bestPoseConv?.tagName,
                                                sub: `${insights.bestPoseConv?.orderConversionPercent}% conv · ${insights.bestPoseConv?.eventsCount?.toLocaleString()} events`,
                                                color: '#0891b2',
                                            },
                                        ].map(card => (
                                            <Grid item xs={12} sm={6} md={4} lg={2} key={card.label}>
                                                <Paper sx={{ p: 2, borderTop: `3px solid ${card.color}`, height: '100%' }}>
                                                    <Typography sx={{ fontSize: 22, lineHeight: 1 }}>{card.icon}</Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{card.label}</Typography>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: card.color, textTransform: 'capitalize', mt: 0.25 }}>
                                                        {card.value}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">{card.sub}</Typography>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* ── Dog Breeds ── */}
                            <AnalyticsSection
                                title="🐕 Dog Breeds"
                                tags={categories.dogBreeds}
                                volumeColor="rgba(59,130,246,0.8)"
                                convColor="rgba(16,185,129,0.8)"
                                cartColor="rgba(245,158,11,0.8)"
                                limit={15}
                            />

                            {/* ── Cat Breeds ── */}
                            <AnalyticsSection
                                title="🐱 Cat Breeds"
                                tags={categories.catBreeds}
                                volumeColor="rgba(139,92,246,0.8)"
                                convColor="rgba(236,72,153,0.8)"
                                cartColor="rgba(168,85,247,0.8)"
                                limit={10}
                            />

                            {/* ── Animals Overview ── */}
                            {(categories.otherPetsTags?.length > 0 || categories.wildlifeTags?.length > 0) && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, fontSize: 15 }}>
                                        🐾 Animals Overview
                                    </Typography>
                                    <Grid container spacing={1} sx={{ mb: 1 }}>
                                        {[
                                            {
                                                label: 'Dogs (all breeds)',
                                                value: categories.dogBreeds?.reduce((s: number, t: any) => s + (t.eventsCount || 0), 0) ?? 0,
                                                color: '#3b82f6',
                                            },
                                            {
                                                label: 'Cats (all breeds)',
                                                value: categories.catBreeds?.reduce((s: number, t: any) => s + (t.eventsCount || 0), 0) ?? 0,
                                                color: '#8b5cf6',
                                            },
                                            {
                                                label: 'Other Pets',
                                                value: categories.otherPetsTags?.reduce((s: number, t: any) => s + (t.eventsCount || 0), 0) ?? 0,
                                                color: '#22c55e',
                                            },
                                            {
                                                label: 'Wildlife & Farm',
                                                value: categories.wildlifeTags?.reduce((s: number, t: any) => s + (t.eventsCount || 0), 0) ?? 0,
                                                color: '#f59e0b',
                                            },
                                        ].map(stat => {
                                            const total = mockupsStats?.tagStats?.totalRecognizedEvents || 0;
                                            const pct = total > 0 ? ((stat.value / total) * 100).toFixed(2) : '—';
                                            return (
                                                <Grid item xs={6} sm={3} key={stat.label}>
                                                    <Paper sx={{ p: 1.5, borderLeft: `3px solid ${stat.color}`, height: '100%' }}>
                                                        <Typography variant="caption" color="text.secondary" display="block">{stat.label}</Typography>
                                                        <Typography variant="h6" sx={{ color: stat.color, fontWeight: 700, lineHeight: 1.2 }}>
                                                            {stat.value.toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {pct}% of recognized
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            )}

                            {/* ── Other Pets & Small Animals ── */}
                            <AnalyticsSection
                                title="🐾 Other Pets & Small Animals"
                                tags={categories.otherPetsTags}
                                volumeColor="rgba(34,197,94,0.8)"
                                convColor="rgba(5,150,105,0.8)"
                                cartColor="rgba(16,185,129,0.8)"
                                limit={15}
                            />

                            {/* ── Wildlife & Farm Animals ── */}
                            <AnalyticsSection
                                title="🦁 Wildlife & Farm Animals"
                                tags={categories.wildlifeTags}
                                volumeColor="rgba(245,158,11,0.8)"
                                convColor="rgba(217,119,6,0.8)"
                                cartColor="rgba(253,186,116,0.8)"
                                limit={20}
                            />

                            {/* ── People Groups ── */}
                            <AnalyticsSection
                                title="👥 People Groups"
                                tags={categories.peopleGroups}
                                volumeColor="rgba(239,68,68,0.8)"
                                convColor="rgba(244,63,94,0.8)"
                                cartColor="rgba(251,113,133,0.8)"
                                limit={15}
                            />

                            {/* ── Pose Types ── */}
                            <AnalyticsSection
                                title="🧘 Pose Types"
                                tags={categories.poseTypes}
                                volumeColor="rgba(20,184,166,0.8)"
                                convColor="rgba(234,179,8,0.8)"
                                cartColor="rgba(6,182,212,0.8)"
                                limit={15}
                            />

                            {/* ── Colors ── */}
                            <AnalyticsSection
                                title="🎨 Colors"
                                tags={categories.colorTags}
                                volumeColor="rgba(99,102,241,0.8)"
                                convColor="rgba(67,56,202,0.8)"
                                cartColor="rgba(129,140,248,0.8)"
                                limit={15}
                            />

                            {/* ── Themes & Accessories ── */}
                            <AnalyticsSection
                                title="🎉 Themes & Accessories"
                                tags={categories.themeTags}
                                volumeColor="rgba(249,115,22,0.8)"
                                convColor="rgba(234,88,12,0.8)"
                                cartColor="rgba(253,186,116,0.8)"
                                limit={15}
                            />

                            {/* ── Clothing ── */}
                            <AnalyticsSection
                                title="👗 Clothing & Accessories"
                                tags={categories.clothingTags}
                                volumeColor="rgba(14,165,233,0.8)"
                                convColor="rgba(2,132,199,0.8)"
                                cartColor="rgba(56,189,248,0.8)"
                                limit={15}
                            />

                            {/* ── Top 20 Converters (min 1000 events) ── */}
                            {topConverters.length > 0 && (
                                <Box sx={{ mb: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: 15 }}>
                                        🏅 Top 20 Tags by Conversion Rate <Typography component="span" variant="caption" color="text.secondary">(min 1,000 events)</Typography>
                                    </Typography>
                                    <Paper sx={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                            <thead>
                                                <tr style={{ background: theme.palette.action.hover }}>
                                                    <th style={{ padding: '8px', textAlign: 'center' }}>#</th>
                                                    <th align="left" style={{ padding: '8px 12px' }}>Tag</th>
                                                    <th style={{ padding: '8px' }}>Category</th>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>Events</th>
                                                    <th style={{ padding: '8px', textAlign: 'right' }}>Cart %</th>
                                                    <th style={{ padding: '8px', textAlign: 'center' }}>Conv. %</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {topConverters.map((tag, i) => {
                                                    const conv = parseFloat(tag.orderConversionPercent);
                                                    const cat = getTagCategory(tag.tagName);
                                                    return (
                                                        <tr
                                                            key={tag.tagId}
                                                            style={{ background: i % 2 === 0 ? 'transparent' : theme.palette.action.hover }}
                                                        >
                                                            <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: i < 3 ? 700 : 400, color: i === 0 ? '#f59e0b' : i === 1 ? '#94a3b8' : i === 2 ? '#b45309' : undefined }}>
                                                                {i + 1}
                                                            </td>
                                                            <td style={{ padding: '5px 12px', fontWeight: 500 }}>{tag.tagName}</td>
                                                            <td style={{ padding: '5px 8px' }}>
                                                                <Chip
                                                                    label={cat}
                                                                    size="small"
                                                                    sx={{
                                                                        fontSize: 10,
                                                                        height: 18,
                                                                        bgcolor: `${CATEGORY_COLORS[cat]}22`,
                                                                        color: CATEGORY_COLORS[cat],
                                                                        fontWeight: 600,
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={{ padding: '5px 8px', textAlign: 'right' }}>{tag.eventsCount?.toLocaleString()}</td>
                                                            <td style={{ padding: '5px 8px', textAlign: 'right', color: parseFloat(tag.addToCartPercent) >= 35 ? '#16a34a' : undefined }}>
                                                                {tag.addToCartPercent}%
                                                            </td>
                                                            <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                                                <Chip
                                                                    label={`${tag.orderConversionPercent}%`}
                                                                    size="small"
                                                                    color={conv >= 18 ? 'success' : 'primary'}
                                                                    sx={{ fontWeight: 700 }}
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </Paper>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {status === 'loading' && (
                <Skeleton animation="wave" width="100%" height={600} variant="rounded" />
            )}

            {/* ── Recognition Dialog ── */}
            <Dialog open={recognizeDialogOpen} onClose={handleCloseRecognitionDialog} fullWidth maxWidth="xs">
                <DialogTitle>Recognize Images</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        Each day from the selected range will be sent as a separate request with a delay of {REQUEST_DELAY_MS} ms.
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={splitByHalfDay}
                                onChange={(e) => setSplitByHalfDay(e.target.checked)}
                                disabled={recognizeLoading}
                            />
                        }
                        label="Split each day into two requests (00:00-11:59 and 12:00-23:59)"
                        sx={{ mt: 2 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 2 }}>
                        <TextField
                            label="Date From"
                            type="date"
                            name="minDate"
                            value={recognizeDates.minDate}
                            onChange={handleRecognitionDateChange}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            disabled={recognizeLoading}
                        />
                        <TextField
                            label="Date To"
                            type="date"
                            name="maxDate"
                            value={recognizeDates.maxDate}
                            onChange={handleRecognitionDateChange}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            disabled={recognizeLoading}
                        />
                    </Box>
                    {recognizeError && (
                        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                            {recognizeError}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCloseRecognitionDialog()} disabled={recognizeLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleTriggerRecognition} variant="contained" disabled={recognizeLoading}>
                        {recognizeLoading ? <CircularProgress size={20} /> : 'Run'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
};

export default MockupsStatsPage;
